/**
 * The body `POST /api/v1/dev/transactions` expects.
 *
 * Three callers send it — the e2e `seed` fixture, `pnpm seed`, and the dev panel's Seed tab — and
 * the part names bind to properties on the C# `SeedTransactionRequest`. Rename one on a single side
 * and ASP.NET model binding silently ignores that part: a 200, and a transaction missing the state
 * it was supposed to carry. So the names live here, once, and each caller supplies bytes.
 *
 * `FormData` and `Blob` are globals in both runtimes, which is what lets Node and the browser share
 * this rather than keeping two copies in lockstep.
 */

export interface SeedRequestParts {
  /** Source document — PDF for DGD, RTF for DCM. */
  source: Blob;
  sourceFilename: string;
  document: Blob;
  layout?: Blob;
  /** Post-extraction state to replay, keyed by artifact filename (`curated.json`, …). */
  overlays?: Record<string, Blob>;
  /** The enum name, or the raw integer an artifact records — the API binds either. */
  documentType: string;
  /** Makes the row identifiable in the transaction list if a delete is ever missed. */
  displayName?: string;
  regulations?: readonly string[];
}

export const jsonBlob = (value: unknown): Blob =>
  new Blob([JSON.stringify(value)], { type: 'application/json' });

export function seedMultipart(parts: SeedRequestParts): FormData {
  const form = new FormData();
  form.set('File', parts.source, parts.sourceFilename);
  form.set('Document', parts.document, 'document.json');
  if (parts.layout) form.set('Layout', parts.layout, 'layout_extracted.json');

  for (const [filename, blob] of Object.entries(parts.overlays ?? {})) {
    form.append('Artifacts', blob, filename);
  }

  form.set('DocumentType', parts.documentType);
  if (parts.displayName) form.set('DisplayName', parts.displayName);
  for (const regulation of parts.regulations ?? []) form.append('Regulations', regulation);
  return form;
}

export const reproDisplayName = (origin: string): string => `Repro ${origin.slice(0, 8)}`;

export const STAND_IN_PDF_NAME = 'stand-in.pdf';
